import type {
  SnapBrowserBootstrap,
  SnapCapturePolicy,
  SnapCategoryOption,
  SnapEvidenceConstraints,
  SnapExperienceConfiguration,
  SnapPriorityOption,
  SnapPrivacyRedactionPolicy,
  SnapUiConfiguration,
  SnapEndpointDiscovery
} from '../types';
import {
  SNAP_CONTRACT_FAMILY,
  SNAP_CONTRACT_VERSION,
  SnapDenyCodes
} from '../types';
import { createSnapError, DefaultDeny, isPlainObject } from '../security/sanitize';
import type { SnapError } from '../types';

const FORBIDDEN_BROWSER_KEYS = [
  'appKey',
  'AppKey',
  'appSecret',
  'AppSecret',
  'signingKey',
  'SigningKey',
  'clientSecret',
  'ClientSecret'
];

export interface BootstrapValidationResult {
  ok: true;
  bootstrap: SnapBrowserBootstrap;
  defaults: {
    ui: SnapUiConfiguration;
    capturePolicy: SnapCapturePolicy;
    privacyPolicy: SnapPrivacyRedactionPolicy;
    evidenceConstraints: SnapEvidenceConstraints;
    endpoints: SnapEndpointDiscovery;
    experience: SnapExperienceConfiguration;
  };
}

/** PR-8.7C canonical priority ladder — mirrors NoeVault SupportTicket priority values. */
export const DEFAULT_PRIORITY_OPTIONS: SnapPriorityOption[] = [
  { value: 1, label: 'Critical' },
  { value: 2, label: 'High' },
  { value: 3, label: 'Normal' },
  { value: 4, label: 'Low' }
];

export const DEFAULT_PRIORITY = 3;
export const DEFAULT_MAX_ATTACHMENT_COUNT = 5;

export const DEFAULT_MY_REQUESTS_PATH = '/api/v1/SupportTickets/MyTickets';
export const DEFAULT_REQUEST_DETAIL_PATH_TEMPLATE =
  '/api/v1/SupportTickets/GetDetails/{id}';
export const DEFAULT_COMMENT_PATH = '/api/v1/SupportTickets/comment';

/** PR-8.7D.5 — optional account claim / Open in NoeVault. */
export const DEFAULT_CLAIM_INVITE_PATH = '/api/v1/snap-integration/claim/invite';
export const DEFAULT_CLAIM_COMPLETE_PATH = '/api/v1/snap-integration/claim/complete';

export interface BootstrapValidationFailure {
  ok: false;
  error: SnapError;
}

export type BootstrapValidation =
  | BootstrapValidationResult
  | BootstrapValidationFailure;

function normalizeGuid(value: unknown): string {
  return String(value ?? '').trim();
}

function asBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asStringArray(value: unknown, fallback: string[]): string[] {
  return Array.isArray(value)
    ? value.map((v) => String(v)).filter(Boolean)
    : fallback;
}

function pickCamelOrPascal<T>(
  obj: Record<string, unknown>,
  camel: string,
  pascal: string
): T | undefined {
  if (camel in obj) return obj[camel] as T;
  if (pascal in obj) return obj[pascal] as T;
  return undefined;
}

function asDisplayName(value: unknown): string | null {
  const text = String(value ?? '').trim();
  return text ? text.slice(0, 200) : null;
}

function asOptionalString(value: unknown): string | undefined {
  const text = String(value ?? '').trim();
  return text ? text.slice(0, 120) : undefined;
}

function normalizePriorityOptions(value: unknown): SnapPriorityOption[] {
  if (!Array.isArray(value)) return DEFAULT_PRIORITY_OPTIONS.map((o) => ({ ...o }));
  const options = value
    .map((item) => {
      const o = isPlainObject(item) ? item : {};
      const numeric = Number(pickCamelOrPascal(o, 'value', 'Value'));
      const label = asOptionalString(pickCamelOrPascal(o, 'label', 'Label'));
      if (!Number.isFinite(numeric) || !label) return null;
      return { value: numeric, label } satisfies SnapPriorityOption;
    })
    .filter((o): o is SnapPriorityOption => o !== null);
  return options.length ? options : DEFAULT_PRIORITY_OPTIONS.map((o) => ({ ...o }));
}

function normalizePriority(value: unknown): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? Math.trunc(numeric) : DEFAULT_PRIORITY;
}

function normalizeAttachmentCount(value: unknown): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0
    ? Math.trunc(numeric)
    : DEFAULT_MAX_ATTACHMENT_COUNT;
}

function normalizeCategories(value: unknown): SnapCategoryOption[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const o = isPlainObject(item) ? item : {};
      const id = Number(
        pickCamelOrPascal(o, 'id', 'Id') ??
          pickCamelOrPascal(o, 'categoryId', 'CategoryId')
      );
      const name = asOptionalString(
        pickCamelOrPascal(o, 'name', 'Name') ??
          pickCamelOrPascal(o, 'categoryName', 'CategoryName')
      );
      if (!Number.isFinite(id) || !name) return null;
      return { id, name } satisfies SnapCategoryOption;
    })
    .filter((o): o is SnapCategoryOption => o !== null);
}

/** Normalize PR-8.6C camelCase or .NET PascalCase projection JSON. */
export function normalizeBootstrap(raw: unknown): SnapBrowserBootstrap {
  if (!isPlainObject(raw)) {
    throw createSnapError(
      'bootstrap_invalid',
      SnapDenyCodes.BootstrapInvalid,
      'Bootstrap must be an object.'
    );
  }

  const capabilityRaw =
    (pickCamelOrPascal<Record<string, unknown>>(raw, 'capability', 'Capability') as
      | Record<string, unknown>
      | undefined) ?? {};
  const contextRaw =
    (pickCamelOrPascal<Record<string, unknown>>(raw, 'context', 'Context') as
      | Record<string, unknown>
      | undefined) ?? {};
  const uiRaw = pickCamelOrPascal<Record<string, unknown>>(raw, 'ui', 'Ui');
  const captureRaw = pickCamelOrPascal<Record<string, unknown>>(
    raw,
    'capturePolicy',
    'CapturePolicy'
  );
  const privacyRaw = pickCamelOrPascal<Record<string, unknown>>(
    raw,
    'privacyPolicy',
    'PrivacyPolicy'
  );
  const evidenceRaw = pickCamelOrPascal<Record<string, unknown>>(
    raw,
    'evidenceConstraints',
    'EvidenceConstraints'
  );
  const endpointsRaw = pickCamelOrPascal<Record<string, unknown>>(
    raw,
    'endpoints',
    'Endpoints'
  );
  const experienceRaw = pickCamelOrPascal<Record<string, unknown>>(
    raw,
    'experience',
    'Experience'
  );
  const claimRaw = pickCamelOrPascal<Record<string, unknown>>(raw, 'claim', 'Claim');

  const expiresAt =
    pickCamelOrPascal<string>(raw, 'expiresAtUtc', 'ExpiresAtUtc') ?? '';

  return {
    contractVersion: String(
      pickCamelOrPascal(raw, 'contractVersion', 'ContractVersion') ?? ''
    ),
    correlationId: String(
      pickCamelOrPascal(raw, 'correlationId', 'CorrelationId') ?? ''
    ),
    authorization: (pickCamelOrPascal(raw, 'authorization', 'Authorization') as
      | string
      | null
      | undefined) ?? null,
    expiresAtUtc: String(expiresAt),
    expiresInSeconds: Number(
      pickCamelOrPascal(raw, 'expiresInSeconds', 'ExpiresInSeconds') ?? 0
    ),
    capability: {
      capabilityKey: String(
        pickCamelOrPascal(capabilityRaw, 'capabilityKey', 'CapabilityKey') ??
          'support.snap'
      ),
      commercialKey: String(
        pickCamelOrPascal(capabilityRaw, 'commercialKey', 'CommercialKey') ??
          'support_snap'
      ),
      available: asBool(
        pickCamelOrPascal(capabilityRaw, 'available', 'Available'),
        false
      ),
      state: String(pickCamelOrPascal(capabilityRaw, 'state', 'State') ?? 'Unavailable'),
      softDisabled: asBool(
        pickCamelOrPascal(capabilityRaw, 'softDisabled', 'SoftDisabled'),
        false
      ),
      createAllowed: asBool(
        pickCamelOrPascal(capabilityRaw, 'createAllowed', 'CreateAllowed'),
        false
      ),
      myRequestsAllowed: asBool(
        pickCamelOrPascal(capabilityRaw, 'myRequestsAllowed', 'MyRequestsAllowed'),
        asBool(pickCamelOrPascal(capabilityRaw, 'createAllowed', 'CreateAllowed'), false)
      ),
      captureAllowed: asBool(
        pickCamelOrPascal(capabilityRaw, 'captureAllowed', 'CaptureAllowed'),
        true
      ),
      evidenceAllowed: asBool(
        pickCamelOrPascal(capabilityRaw, 'evidenceAllowed', 'EvidenceAllowed'),
        true
      )
    },
    context: {
      organisationUid: normalizeGuid(
        pickCamelOrPascal(contextRaw, 'organisationUid', 'OrganisationUid')
      ),
      solutionUid: normalizeGuid(
        pickCamelOrPascal(contextRaw, 'solutionUid', 'SolutionUid')
      ),
      userUid: normalizeGuid(
        pickCamelOrPascal(contextRaw, 'userUid', 'UserUid')
      ) || null,
      environmentUid: normalizeGuid(
        pickCamelOrPascal(contextRaw, 'environmentUid', 'EnvironmentUid')
      ) || null,
      componentUid: normalizeGuid(
        pickCamelOrPascal(contextRaw, 'componentUid', 'ComponentUid')
      ) || null,
      runtimeRegistrationUid: normalizeGuid(
        pickCamelOrPascal(
          contextRaw,
          'runtimeRegistrationUid',
          'RuntimeRegistrationUid'
        )
      ) || null,
      organisationDisplayName: asDisplayName(
        pickCamelOrPascal(
          contextRaw,
          'organisationDisplayName',
          'OrganisationDisplayName'
        )
      ),
      solutionDisplayName: asDisplayName(
        pickCamelOrPascal(contextRaw, 'solutionDisplayName', 'SolutionDisplayName')
      )
    },
    ui: uiRaw
      ? {
          launcherEnabled: asBool(
            pickCamelOrPascal(uiRaw, 'launcherEnabled', 'LauncherEnabled'),
            true
          ),
          defaultPlacement: String(
            pickCamelOrPascal(uiRaw, 'defaultPlacement', 'DefaultPlacement') ??
              'bottom-right'
          ),
          assistEnabled: asBool(
            pickCamelOrPascal(uiRaw, 'assistEnabled', 'AssistEnabled'),
            false
          ),
          consentMessage: (pickCamelOrPascal(
            uiRaw,
            'consentMessage',
            'ConsentMessage'
          ) as string | null | undefined) ?? null,
          productName:
            asOptionalString(pickCamelOrPascal(uiRaw, 'productName', 'ProductName')) ??
            'Snap Support',
          reportLabel:
            asOptionalString(pickCamelOrPascal(uiRaw, 'reportLabel', 'ReportLabel')) ??
            'Report an issue',
          myRequestsLabel:
            asOptionalString(
              pickCamelOrPascal(uiRaw, 'myRequestsLabel', 'MyRequestsLabel')
            ) ?? 'My Requests'
        }
      : null,
    capturePolicy: captureRaw
      ? {
          requiresUserInitiation: asBool(
            pickCamelOrPascal(
              captureRaw,
              'requiresUserInitiation',
              'RequiresUserInitiation'
            ),
            true
          ),
          screenshotEnabled: asBool(
            pickCamelOrPascal(captureRaw, 'screenshotEnabled', 'ScreenshotEnabled'),
            true
          ),
          domMetadataEnabled: asBool(
            pickCamelOrPascal(captureRaw, 'domMetadataEnabled', 'DomMetadataEnabled'),
            true
          ),
          clientErrorCaptureEnabled: asBool(
            pickCamelOrPascal(
              captureRaw,
              'clientErrorCaptureEnabled',
              'ClientErrorCaptureEnabled'
            ),
            false
          ),
          includePageUrl: asBool(
            pickCamelOrPascal(captureRaw, 'includePageUrl', 'IncludePageUrl'),
            true
          ),
          includeQueryString: asBool(
            pickCamelOrPascal(captureRaw, 'includeQueryString', 'IncludeQueryString'),
            false
          ),
          includePageTitle: asBool(
            pickCamelOrPascal(captureRaw, 'includePageTitle', 'IncludePageTitle'),
            true
          )
        }
      : null,
    privacyPolicy: privacyRaw
      ? {
          neverCapturePasswords: asBool(
            pickCamelOrPascal(
              privacyRaw,
              'neverCapturePasswords',
              'NeverCapturePasswords'
            ),
            true
          ),
          neverCaptureTokensOrSecrets: asBool(
            pickCamelOrPascal(
              privacyRaw,
              'neverCaptureTokensOrSecrets',
              'NeverCaptureTokensOrSecrets'
            ),
            true
          ),
          maskInputTypes: asStringArray(
            pickCamelOrPascal(privacyRaw, 'maskInputTypes', 'MaskInputTypes'),
            ['password', 'hidden']
          ),
          maskAttributeSelectors: asStringArray(
            pickCamelOrPascal(
              privacyRaw,
              'maskAttributeSelectors',
              'MaskAttributeSelectors'
            ),
            [
              '[data-nv-redact]',
              '[autocomplete=one-time-code]',
              '[name*=token i]',
              '[name*=secret i]',
              '[name*=password i]'
            ]
          ),
          maskCssSelectors: asStringArray(
            pickCamelOrPascal(privacyRaw, 'maskCssSelectors', 'MaskCssSelectors'),
            []
          )
        }
      : null,
    evidenceConstraints: evidenceRaw
      ? {
          maxFileBytes: Number(
            pickCamelOrPascal(evidenceRaw, 'maxFileBytes', 'MaxFileBytes') ??
              20 * 1024 * 1024
          ),
          allowedMimeTypes: asStringArray(
            pickCamelOrPascal(evidenceRaw, 'allowedMimeTypes', 'AllowedMimeTypes'),
            [
              'image/png',
              'image/jpeg',
              'image/webp',
              'image/gif',
              'application/pdf',
              'text/plain'
            ]
          ),
          allowedExtensions: asStringArray(
            pickCamelOrPascal(
              evidenceRaw,
              'allowedExtensions',
              'AllowedExtensions'
            ),
            ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.pdf', '.txt']
          ),
          retentionPolicyRef: String(
            pickCamelOrPascal(
              evidenceRaw,
              'retentionPolicyRef',
              'RetentionPolicyRef'
            ) ?? 'tenant-default'
          ),
          malwareScanRequired: asBool(
            pickCamelOrPascal(
              evidenceRaw,
              'malwareScanRequired',
              'MalwareScanRequired'
            ),
            true
          ),
          maxAttachmentCount: Number(
            pickCamelOrPascal(
              evidenceRaw,
              'maxAttachmentCount',
              'MaxAttachmentCount'
            ) ?? 5
          )
        }
      : null,
    endpoints: endpointsRaw
      ? {
          contractVersion: String(
            pickCamelOrPascal(
              endpointsRaw,
              'contractVersion',
              'ContractVersion'
            ) ?? SNAP_CONTRACT_VERSION
          ),
          bootstrapPath: String(
            pickCamelOrPascal(endpointsRaw, 'bootstrapPath', 'BootstrapPath') ??
              '/api/v1/snap-integration/bootstrap'
          ),
          authorizePath: String(
            pickCamelOrPascal(endpointsRaw, 'authorizePath', 'AuthorizePath') ??
              '/api/v1/snap-integration/authorize'
          ),
          ticketCreatePath: String(
            pickCamelOrPascal(
              endpointsRaw,
              'ticketCreatePath',
              'TicketCreatePath'
            ) ?? '/api/v1/SupportTickets/quick'
          ),
          myRequestsPath: String(
            pickCamelOrPascal(endpointsRaw, 'myRequestsPath', 'MyRequestsPath') ??
              DEFAULT_MY_REQUESTS_PATH
          ),
          requestDetailPathTemplate: String(
            pickCamelOrPascal(
              endpointsRaw,
              'requestDetailPathTemplate',
              'RequestDetailPathTemplate'
            ) ?? DEFAULT_REQUEST_DETAIL_PATH_TEMPLATE
          ),
          commentPath: String(
            pickCamelOrPascal(endpointsRaw, 'commentPath', 'CommentPath') ??
              DEFAULT_COMMENT_PATH
          ),
          attachmentPathTemplate: String(
            pickCamelOrPascal(
              endpointsRaw,
              'attachmentPathTemplate',
              'AttachmentPathTemplate'
            ) ?? '/api/v1/SupportTickets/{ticketId}/attachments'
          ),
          correlationHeaderName: String(
            pickCamelOrPascal(
              endpointsRaw,
              'correlationHeaderName',
              'CorrelationHeaderName'
            ) ?? 'X-Correlation-Id'
          ),
          bootstrapHeaderName: String(
            pickCamelOrPascal(
              endpointsRaw,
              'bootstrapHeaderName',
              'BootstrapHeaderName'
            ) ?? 'X-NoeVault-Snap-Bootstrap'
          )
        }
      : null,
    experience: experienceRaw
      ? {
          categorySupport: asBool(
            pickCamelOrPascal(experienceRaw, 'categorySupport', 'CategorySupport'),
            true
          ),
          prioritySupport: asBool(
            pickCamelOrPascal(experienceRaw, 'prioritySupport', 'PrioritySupport'),
            true
          ),
          defaultPriority: normalizePriority(
            pickCamelOrPascal(experienceRaw, 'defaultPriority', 'DefaultPriority')
          ),
          priorityOptions: normalizePriorityOptions(
            pickCamelOrPascal(experienceRaw, 'priorityOptions', 'PriorityOptions')
          ),
          categories: normalizeCategories(
            pickCamelOrPascal(experienceRaw, 'categories', 'Categories')
          ),
          maxAttachmentCount: normalizeAttachmentCount(
            pickCamelOrPascal(
              experienceRaw,
              'maxAttachmentCount',
              'MaxAttachmentCount'
            )
          )
        }
      : null,
    // PR-8.7D.5 — optional claim offer. Absent means "no offer": embedded Snap is unaffected.
    claim: claimRaw
      ? {
          offered: asBool(pickCamelOrPascal(claimRaw, 'offered', 'Offered'), false),
          alreadyClaimed: asBool(
            pickCamelOrPascal(claimRaw, 'alreadyClaimed', 'AlreadyClaimed'),
            false
          ),
          requiresEmail: asBool(
            pickCamelOrPascal(claimRaw, 'requiresEmail', 'RequiresEmail'),
            false
          ),
          maskedEmail:
            (pickCamelOrPascal<string>(claimRaw, 'maskedEmail', 'MaskedEmail') ?? null) || null,
          invitePath: String(
            pickCamelOrPascal(claimRaw, 'invitePath', 'InvitePath') ??
              DEFAULT_CLAIM_INVITE_PATH
          ),
          completePath: String(
            pickCamelOrPascal(claimRaw, 'completePath', 'CompletePath') ??
              DEFAULT_CLAIM_COMPLETE_PATH
          ),
          deepLinkTemplate:
            (pickCamelOrPascal<string>(claimRaw, 'deepLinkTemplate', 'DeepLinkTemplate') ??
              null) || null,
          portalCapabilities: asStringArray(
            pickCamelOrPascal(claimRaw, 'portalCapabilities', 'PortalCapabilities'),
            []
          ),
          offerLabel: String(
            pickCamelOrPascal(claimRaw, 'offerLabel', 'OfferLabel') ??
              'Claim your support history'
          ),
          openInNoeVaultLabel: String(
            pickCamelOrPascal(claimRaw, 'openInNoeVaultLabel', 'OpenInNoeVaultLabel') ??
              'Open in NoeVault'
          ),
          offerDescription: String(
            pickCamelOrPascal(claimRaw, 'offerDescription', 'OfferDescription') ?? ''
          )
        }
      : null,
    grantedGrains: asStringArray(
      pickCamelOrPascal(raw, 'grantedGrains', 'GrantedGrains'),
      []
    ),
    optionalContext: Array.isArray(
      pickCamelOrPascal(raw, 'optionalContext', 'OptionalContext')
    )
      ? ((pickCamelOrPascal(raw, 'optionalContext', 'OptionalContext') as unknown[]) ??
          []).map((item) => {
          const o = (item ?? {}) as Record<string, unknown>;
          return {
            field: String(pickCamelOrPascal(o, 'field', 'Field') ?? ''),
            value: normalizeGuid(pickCamelOrPascal(o, 'value', 'Value')) || null,
            accepted: asBool(pickCamelOrPascal(o, 'accepted', 'Accepted'), false),
            reason: String(pickCamelOrPascal(o, 'reason', 'Reason') ?? '')
          };
        })
      : [],
    sdkPackageId: String(
      pickCamelOrPascal(raw, 'sdkPackageId', 'SdkPackageId') ?? ''
    ),
    sdkPackageVersion: String(
      pickCamelOrPascal(raw, 'sdkPackageVersion', 'SdkPackageVersion') ?? ''
    )
  };
}

export function defaultPolicies(): BootstrapValidationResult['defaults'] {
  return {
    ui: {
      launcherEnabled: true,
      defaultPlacement: 'bottom-right',
      assistEnabled: false,
      consentMessage:
        'You are about to send a support request with optional screenshot evidence. Do not include passwords or secrets.',
      productName: 'Snap Support',
      reportLabel: 'Report an issue',
      myRequestsLabel: 'My Requests'
    },
    capturePolicy: {
      requiresUserInitiation: true,
      screenshotEnabled: true,
      domMetadataEnabled: true,
      clientErrorCaptureEnabled: false,
      includePageUrl: true,
      includeQueryString: false,
      includePageTitle: true
    },
    privacyPolicy: {
      neverCapturePasswords: true,
      neverCaptureTokensOrSecrets: true,
      maskInputTypes: ['password', 'hidden'],
      maskAttributeSelectors: [
        '[data-nv-redact]',
        '[data-no-capture]',
        '[data-no-screenshot]',
        '[autocomplete=one-time-code]',
        '[name*=token i]',
        '[name*=secret i]',
        '[name*=password i]'
      ],
      maskCssSelectors: []
    },
    evidenceConstraints: {
      maxFileBytes: 20 * 1024 * 1024,
      allowedMimeTypes: [
        'image/png',
        'image/jpeg',
        'image/webp',
        'image/gif',
        'application/pdf',
        'text/plain'
      ],
      allowedExtensions: ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.pdf', '.txt'],
      retentionPolicyRef: 'tenant-default',
      malwareScanRequired: true,
      maxAttachmentCount: DEFAULT_MAX_ATTACHMENT_COUNT
    },
    endpoints: {
      contractVersion: SNAP_CONTRACT_VERSION,
      bootstrapPath: '/api/v1/snap-integration/bootstrap',
      authorizePath: '/api/v1/snap-integration/authorize',
      ticketCreatePath: '/api/v1/SupportTickets/quick',
      myRequestsPath: DEFAULT_MY_REQUESTS_PATH,
      requestDetailPathTemplate: DEFAULT_REQUEST_DETAIL_PATH_TEMPLATE,
      commentPath: DEFAULT_COMMENT_PATH,
      attachmentPathTemplate: '/api/v1/SupportTickets/{ticketId}/attachments',
      correlationHeaderName: 'X-Correlation-Id',
      bootstrapHeaderName: 'X-NoeVault-Snap-Bootstrap'
    },
    experience: {
      categorySupport: true,
      prioritySupport: true,
      defaultPriority: DEFAULT_PRIORITY,
      priorityOptions: [...DEFAULT_PRIORITY_OPTIONS],
      categories: [],
      maxAttachmentCount: DEFAULT_MAX_ATTACHMENT_COUNT
    }
  };
}

function contractCompatible(version: string): boolean {
  const v = (version || '').trim().toLowerCase();
  if (!v) return false;
  if (v === SNAP_CONTRACT_VERSION) return true;
  // Accept compatible v1 minors like snap-integration.v1.1 conceptually as same major family.
  return v.startsWith(`${SNAP_CONTRACT_FAMILY}.v1`);
}

export function validateBootstrap(
  raw: unknown,
  now: () => Date = () => new Date()
): BootstrapValidation {
  if (!isPlainObject(raw)) {
    return {
      ok: false,
      error: createSnapError(
        'bootstrap_invalid',
        SnapDenyCodes.BootstrapInvalid,
        'Bootstrap must be an object.'
      )
    };
  }

  for (const key of FORBIDDEN_BROWSER_KEYS) {
    if (key in raw && raw[key] != null && String(raw[key]).length > 0) {
      return { ok: false, error: DefaultDeny.appKey() };
    }
  }

  let bootstrap: SnapBrowserBootstrap;
  try {
    bootstrap = normalizeBootstrap(raw);
  } catch (err) {
    return {
      ok: false,
      error:
        err && typeof err === 'object' && 'kind' in err
          ? (err as SnapError)
          : createSnapError(
              'bootstrap_invalid',
              SnapDenyCodes.BootstrapInvalid,
              'Malformed bootstrap projection.'
            )
    };
  }

  if (!contractCompatible(bootstrap.contractVersion)) {
    return {
      ok: false,
      error: createSnapError(
        'contract_mismatch',
        SnapDenyCodes.ContractMismatch,
        `Incompatible contract version '${bootstrap.contractVersion}'. Expected ${SNAP_CONTRACT_VERSION}.`,
        { correlationId: bootstrap.correlationId }
      )
    };
  }

  if (!bootstrap.authorization || !String(bootstrap.authorization).trim()) {
    return {
      ok: false,
      error: DefaultDeny.missingAuth(bootstrap.correlationId)
    };
  }

  const expires = Date.parse(bootstrap.expiresAtUtc);
  if (!Number.isFinite(expires) || now().getTime() >= expires) {
    return {
      ok: false,
      error: DefaultDeny.expired(bootstrap.correlationId)
    };
  }

  if (!bootstrap.context.organisationUid || !bootstrap.context.solutionUid) {
    return {
      ok: false,
      error: createSnapError(
        'bootstrap_invalid',
        SnapDenyCodes.BindingUnavailable,
        'Server-authoritative organisation and solution context are required.',
        { correlationId: bootstrap.correlationId }
      )
    };
  }

  if (!bootstrap.endpoints) {
    return {
      ok: false,
      error: createSnapError(
        'configuration_invalid',
        SnapDenyCodes.ConfigurationInvalid,
        'Endpoint discovery is required.',
        { correlationId: bootstrap.correlationId }
      )
    };
  }

  const defaults = defaultPolicies();
  return {
    ok: true,
    bootstrap,
    defaults: {
      ui: { ...defaults.ui, ...(bootstrap.ui ?? {}) },
      capturePolicy: { ...defaults.capturePolicy, ...(bootstrap.capturePolicy ?? {}) },
      privacyPolicy: {
        ...defaults.privacyPolicy,
        ...(bootstrap.privacyPolicy ?? {}),
        maskInputTypes:
          bootstrap.privacyPolicy?.maskInputTypes?.length
            ? bootstrap.privacyPolicy.maskInputTypes
            : defaults.privacyPolicy.maskInputTypes,
        maskAttributeSelectors:
          bootstrap.privacyPolicy?.maskAttributeSelectors?.length
            ? bootstrap.privacyPolicy.maskAttributeSelectors
            : defaults.privacyPolicy.maskAttributeSelectors,
        maskCssSelectors:
          bootstrap.privacyPolicy?.maskCssSelectors ??
          defaults.privacyPolicy.maskCssSelectors
      },
      evidenceConstraints: {
        ...defaults.evidenceConstraints,
        ...(bootstrap.evidenceConstraints ?? {})
      },
      endpoints: { ...defaults.endpoints, ...bootstrap.endpoints },
      experience: {
        ...defaults.experience,
        ...(bootstrap.experience ?? {}),
        priorityOptions:
          bootstrap.experience?.priorityOptions?.length
            ? bootstrap.experience.priorityOptions
            : defaults.experience.priorityOptions,
        categories: bootstrap.experience?.categories ?? defaults.experience.categories
      }
    }
  };
}

/** Authorization must remain memory-only — never write to Web Storage. */
export function assertAuthorizationNotPersisted(authorization: string | null | undefined): void {
  if (!authorization) return;
  try {
    if (typeof localStorage !== 'undefined') {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i) || '';
        const val = localStorage.getItem(key) || '';
        if (val.includes(authorization)) {
          throw new Error('Authorization leaked into localStorage.');
        }
      }
    }
    if (typeof sessionStorage !== 'undefined') {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i) || '';
        const val = sessionStorage.getItem(key) || '';
        if (val.includes(authorization)) {
          throw new Error('Authorization leaked into sessionStorage.');
        }
      }
    }
  } catch (err) {
    if (err instanceof Error && err.message.includes('Authorization leaked')) throw err;
  }
}
